import { useMemo, useState } from "react";
import { Form, Pagination, Table } from "react-bootstrap";
import { FaExclamationCircle } from "react-icons/fa";

const CommonTableModule = ({
  columns,
  data,
  striped = true,
  bordered = false,
  hover = true,
  responsive = true,
  itemsPerPage = 10,
  isSearchBoxEnabled = true,
  defaultSort = null
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  // 🔹 Step 1: Filtered Data (Search)
  const filteredData = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return data?.filter((row) => {
      const fullNameMatch = row.user?.fullName?.toLowerCase().includes(search);
      const columnMatch = columns.some((column) => {
        const value = row[column.key];
        return value?.toString().toLowerCase().includes(search);
      });
      return fullNameMatch || columnMatch;
    });
  }, [searchTerm, data, columns]);

  // 🔹 Step 2: Sorting Logic
  const sortedData = useMemo(() => {
    if (!defaultSort) return filteredData;

    const { key, order } = defaultSort;
    return [...filteredData].sort((a, b) => {
      const valA = a[key]?.toString().toLowerCase() || "";
      const valB = b[key]?.toString().toLowerCase() || "";

      if (order === "asc") return valA.localeCompare(valB);
      if (order === "desc") return valB.localeCompare(valA);
      return 0;
    });
  }, [filteredData, defaultSort]);

  // 🔹 Step 3: Pagination
  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedData?.slice(start, start + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const renderPagination = () => (
    <Pagination className="justify-content-center mt-4">
      {Array.from({ length: totalPages }, (_, i) => (
        <Pagination.Item
          key={i + 1}
          active={i + 1 === currentPage}
          onClick={() => setCurrentPage(i + 1)}
        >
          {i + 1}
        </Pagination.Item>
      ))}
    </Pagination>
  );

  return (
    <div className="common-table-wrapper shadow-sm p-4 bg-white rounded">
      {isSearchBoxEnabled && (
        <div className="d-flex justify-content-end mb-3">
          <Form.Group controlId="searchInput" className="w-100" style={{ maxWidth: "300px" }}>
            <div className="input-group custom-search-box">
              <span className="input-group-text">
                <i className="fa fa-search text-muted" />
              </span>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </Form.Group>
        </div>
      )}

      <div className="table-responsive">
        <Table
          striped={striped}
          bordered={bordered}
          hover={hover}
          className="custom-table align-middle mb-0"
        >
          <thead className="table-header text-uppercase text-muted">
            <tr>
              {columns?.map((column) => (
                <th key={column.key} className="text-center">{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData?.length > 0 ? (
              paginatedData?.map((row, rowIndex) => (
                <tr key={row.id || rowIndex} className="table-row">
                  {columns?.map((column) => (
                    <td
                      key={`${row.id || rowIndex}-${column.key}`}
                      className="text-center"
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key] || "—"}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns?.length} className="text-center py-4 text-muted">
                  <FaExclamationCircle size={20} className="me-2" />
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && renderPagination()}
    </div>
  );
};

export default CommonTableModule;
